import React from 'react';
import { Box, Typography } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ShowChartIcon from '@mui/icons-material/ShowChart';

const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

type MarketplaceNavProps = {
  activeTab: number;
  onSelect: (value: number) => void;
  activeListings: number;
  myListingsCount: number;
  ownedCount: number;
  salesCount: number;
};

// The marketplace "dashboard" nav — four stat tiles that replace the old tab
// bar. Each tile shows a live count and doubles as the section switcher (the
// active tile is filled cyan).
export const MarketplaceNav = ({
  activeTab,
  onSelect,
  activeListings,
  myListingsCount,
  ownedCount,
  salesCount
}: MarketplaceNavProps) => {
  const tiles = [
    {
      value: 0,
      label: 'Browse Market',
      icon: <StorefrontIcon />,
      count: activeListings,
      unit: 'listings'
    },
    {
      value: 1,
      label: 'My Listings',
      icon: <LocalOfferIcon />,
      count: myListingsCount,
      unit: 'active'
    },
    {
      value: 2,
      label: 'List a Card',
      icon: <AddCircleOutlineIcon />,
      count: ownedCount,
      unit: 'cards'
    },
    {
      value: 3,
      label: 'Activity',
      icon: <ShowChartIcon />,
      count: salesCount,
      unit: 'sales'
    }
  ];

  return (
    <Box
      sx={{
        mb: 3,
        display: 'grid',
        gap: { xs: 1.25, md: 2 },
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }
      }}
    >
      {tiles.map((t) => {
        const active = activeTab === t.value;
        return (
          <Box
            key={t.value}
            role="button"
            tabIndex={0}
            aria-pressed={active}
            onClick={() => onSelect(t.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(t.value);
              }
            }}
            sx={{
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '14px',
              // a little tighter on a phone, so a 320px screen's ~130px tile
              // still leaves the text room for "LISTINGS" (see the count below)
              p: { xs: 1.5, sm: 1.75, md: 2.25 },
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.25, sm: 1.75 },
              border: active
                ? '1px solid rgba(56, 232, 255, 0.6)'
                : '1px solid rgba(140, 170, 190, 0.14)',
              background: active
                ? 'linear-gradient(180deg, rgba(56, 232, 255, 0.14), rgba(11, 17, 25, 0.55))'
                : 'linear-gradient(180deg, rgba(11, 17, 25, 0.72), rgba(7, 11, 17, 0.82))',
              boxShadow: active
                ? '0 0 0 1px rgba(56, 232, 255, 0.25), 0 18px 40px -26px rgba(0, 0, 0, 0.9)'
                : '0 18px 40px -30px rgba(0, 0, 0, 0.9)',
              transition:
                'border-color .15s ease, transform .15s ease, background .15s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                borderColor: 'rgba(56, 232, 255, 0.5)'
              }
            }}
          >
            <Box
              sx={{
                width: { xs: 36, sm: 42 },
                height: { xs: 36, sm: 42 },
                flexShrink: 0,
                borderRadius: '11px',
                display: 'grid',
                placeItems: 'center',
                color: active ? '#04222a' : '#38e8ff',
                background: active ? '#38e8ff' : 'rgba(56, 232, 255, 0.1)',
                border: active ? 'none' : '1px solid rgba(56, 232, 255, 0.25)'
              }}
            >
              {t.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  font: `800 9px ${MONO}`,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: active ? '#38e8ff' : '#8ea3b3',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t.label}
              </Typography>
              {/* The count and its unit share a line where they fit and the
                  unit drops under the count where they don't. They used to be
                  one unbreakable run (no space between them, only a margin), so
                  on most phones the tile cut "LISTINGS" off mid-word. */}
              <Typography
                sx={{
                  mt: 0.25,
                  fontWeight: 900,
                  fontSize: 20,
                  lineHeight: 1,
                  color: '#e8f4fb',
                  fontVariantNumeric: 'tabular-nums',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'baseline',
                  columnGap: 0.75,
                  rowGap: 0.25
                }}
              >
                {Number(t.count || 0).toLocaleString('en-AU')}
                <Box
                  component="span"
                  sx={{
                    font: `700 10px ${MONO}`,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#5c6b78'
                  }}
                >
                  {t.unit}
                </Box>
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
